"use client";

import { LazyMotion, domAnimation } from "motion/react";

/**
 * Feature provider for the `m` components used across the site.
 *
 * Every animated element imports `m` rather than `motion`. The difference is
 * what gets bundled: `motion.div` statically pulls in the entire feature set —
 * layout projection, drag, pan, path drawing, the lot — whether the element
 * uses them or not, because the import is what wires the features in. `m` ships
 * only the renderer, and this provider supplies the feature bundle once for the
 * whole tree.
 *
 * `domAnimation` covers what the site actually does: animate/variants,
 * `AnimatePresence` exits, and the hover / tap / focus / `whileInView` gestures.
 * The scroll hooks (`useScroll`, `useTransform`, `MotionValue`) are standalone
 * and need no bundle at all.
 *
 * It deliberately does NOT include layout animations or drag — that would mean
 * `domMax`, which gives back most of the saving. Nothing needs them: the two
 * pill toggles that used `layout` now animate `x` instead (see
 * app/(site)/pricing/components/shared/pricing-tabs.tsx), and the blog
 * carousel's click-drag is a plain mouse handler, not motion's `drag`.
 *
 * `strict` is the guard rail that keeps the saving from quietly eroding: it
 * throws if a `motion` component is ever rendered inside this tree. Because 71
 * routes are prerendered at build time, a stray `motion.div` fails `next build`
 * rather than shipping and silently re-adding the full bundle.
 */
export default function MotionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <LazyMotion features={domAnimation} strict>
      {children}
    </LazyMotion>
  );
}
